/**
 * ConfigurationService - Handles loading and processing UI configurations
 * Centralizes the complex logic for processing configurations from different formats
 */

import { APIService } from './APIService.js';

/**
 * Process a raw configuration into a standardized format
 * Handles both new and legacy configuration formats
 * 
 * @param {Object} rawConfiguration - Raw configuration from API
 * @param {Function} mergeFunction - Function to merge vehicle features with components
 * @returns {Object} - Processed configuration
 */
export async function processConfiguration(rawConfiguration, mergeFunction) {
  console.log('🔧 ConfigurationService: Processing configuration:', rawConfiguration.name);
  
  // Import Page class - need to do dynamic import to avoid circular dependency
  const { default: Page } = await import('../components/Page');
  
  let loadedPages = new Map();
  let activePageName = 'Main';
    // Canvas properties
  let canvasProperties = { backgroundColor: '#383838', backgroundType: 'solid' };
  let canvasSize = { width: 1024, height: 768 };

  // Handle different configuration formats
  if (rawConfiguration.pages && typeof rawConfiguration.pages === 'object') {
    // New multi-page format - merge each component with vehicle features
    for (const [pageName, pageData] of Object.entries(rawConfiguration.pages)) {
      const page = new Page(pageName, pageData.id);
      
      // Safely merge metadata, preserving the initialized structure
      page.metadata = {
        ...page.metadata, // Keep the properly initialized metadata from constructor
        ...pageData.metadata // Override with saved values if they exist
      };
      
      // Restore canvas properties from saved configuration
      if (pageData.canvasProperties) {
        page.updateCanvasProperties(pageData.canvasProperties);
      }
      
      // Restore timeout settings for WCAG 2.2.1 compliance
      if (pageData.timeoutSettings) {
        page.updateTimeoutSettings(pageData.timeoutSettings);
      }
      
      // Process each component with vehicle feature merge
      if (pageData.components && Array.isArray(pageData.components)) {
        for (const comp of pageData.components) {
          const mergedComponent = mergeFunction ? await mergeFunction(comp) : comp;
          page.addComponent(mergedComponent);
        }
      }
      
      loadedPages.set(pageName, page);
    }
    activePageName = rawConfiguration.currentPage || 'Main';
  } else if (rawConfiguration.interface?.components) {
    // Legacy format - merge with vehicle features
    const page = new Page('Main');
    
    for (const comp of rawConfiguration.interface.components) {
      const standardizedComp = {
        id: comp.id,
        type: comp.type,
        position: comp.position,
        size: comp.dimensions,
        style: comp.appearance,
        content: comp.content,
        accessibility: comp.accessibility,
        behavior: comp.behavior
      };
      const mergedComponent = mergeFunction ? await mergeFunction(standardizedComp) : standardizedComp;
      page.addComponent(mergedComponent);
    }
    
    loadedPages.set('Main', page);
  } else if (rawConfiguration.components) {
    // Direct components format - merge with vehicle features
    const page = new Page('Main');
    
    for (const comp of rawConfiguration.components) {
      const mergedComponent = mergeFunction ? await mergeFunction(comp) : comp;
      page.addComponent(mergedComponent);
    }
    
    loadedPages.set('Main', page);
  }

  // Extract canvas size from configuration
  if (rawConfiguration.canvasSize) {
    canvasSize = rawConfiguration.canvasSize;
    console.log('🎨 ConfigurationService: Found canvasSize:', canvasSize);
  } else if (rawConfiguration.canvas) {
    canvasSize = rawConfiguration.canvas;
    console.log('🎨 ConfigurationService: Found canvas:', canvasSize);
  } else {
    console.log('🎨 ConfigurationService: Using default canvas size:', canvasSize);
  }

  return {
    pages: loadedPages,
    activePageName,
    canvasSize,
    canvasProperties
  };
}

/**
 * Load and process a configuration by ID
 * Includes vehicle feature merging
 * 
 * @param {string} configId - Configuration ID to load
 * @returns {Object} - Processed configuration with merged components
 */
export async function loadConfiguration(configId) {
  console.log(`🔧 ConfigurationService: Loading configuration: ${configId}`);
  
  try {
    // Fetch raw configuration
    const rawConfiguration = await APIService.fetchConfiguration(configId);
    console.log('📡 ConfigurationService: Raw configuration loaded:', rawConfiguration);
    
    return rawConfiguration;
  } catch (error) {
    console.error('ConfigurationService: Failed to load configuration:', error);
    return null;
  }
}

/**
 * Load all available configurations
 * 
 * @returns {Array} - Array of configuration summaries
 */
export async function loadAllConfigurations() {
  console.log('🔧 ConfigurationService: Loading all configurations...');
  
  try {
    const response = await APIService.fetchAllConfigurations();
    console.log('✅ ConfigurationService: Loaded', response.configurations?.length || 0, 'configurations');
    return response.configurations || [];
  } catch (error) {
    console.error('ConfigurationService: Failed to load configurations:', error);
    throw error;
  }
}

/**
 * Extract canvas background styles from canvas properties
 * 
 * @param {Object} canvasProperties - Canvas properties object
 * @returns {Object} - CSS styles for canvas background
 */
export function getCanvasBackgroundStyles(canvasProperties) {
  if (!canvasProperties) {
    return { backgroundColor: '#383838' };
  }

  const styles = {
    backgroundColor: canvasProperties.backgroundColor || '#383838'
  };

  if (canvasProperties.backgroundType === 'image' && canvasProperties.backgroundImage) {
    styles.backgroundImage = `url(${canvasProperties.backgroundImage})`;
    styles.backgroundSize = 'cover';
    styles.backgroundPosition = 'center center';
    styles.backgroundRepeat = 'no-repeat';
  }

  return styles;
}

// Export all functions as ConfigurationService object
export const ConfigurationService = {
  processConfiguration,
  loadConfiguration,
  getCanvasBackgroundStyles
};